import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, Send, Clock, MessageCircle, Instagram, Facebook, Linkedin, CheckCircle } from 'lucide-react';
import SEO from './SEO.jsx';
import SupportChatPanel from './SupportChatPanel.jsx';
import { resolveApiUrl } from '../utils/network.js';
import './ContactUs.css';

const ContactUs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [activeTab, setActiveTab] = useState('general');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(resolveApiUrl('/support-chat/contact-request'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          contact: formData.email.trim(),
          note: formData.message.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setIsSubmitting(false);
      setIsSubmitted(true);
      setTimeout(() => {
        setFormData({ name: '', email: '', message: '' });
        setIsSubmitted(false);
      }, 3000);
    } catch (err) {
      setIsSubmitting(false);
      setSubmitError(t('pages.contact.sendError'));
    }
  };

  const contactInfo = useMemo(() => [
    { icon: <Mail size={20} />, title: t('pages.contact.email'), detail: 'greenwicharmenia@gmail.com', color: '#faa002' },
    { icon: <Phone size={20} />, title: t('pages.contact.phone'), detail: '+374 94 577 585', color: '#32614d' },
  ], [t]);

  const socialLinks = [
    { icon: <Instagram size={20} />, label: 'Instagram', href: 'https://www.instagram.com/greenwicharmenia', color: '#f472a8', bg: 'rgba(244, 114, 168, 0.14)' },
    { icon: <Facebook size={20} />, label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=61578377498350', color: '#5b9dff', bg: 'rgba(91, 157, 255, 0.14)' },
    { icon: <Linkedin size={20} />, label: 'LinkedIn', href: 'https://linkedin.com/company/greenwich-armenia', color: '#5cc4ff', bg: 'rgba(92, 196, 255, 0.14)' },
    { icon: <MessageCircle size={20} />, label: 'Telegram', href: 'https://t.me/+_pwnjwFsOmY3ZjI6', color: '#5cd0ef', bg: 'rgba(92, 208, 239, 0.14)' },
  ];

  const faqs = useMemo(() => [
    { category: 'general', q: t('pages.contact.faqG1q'), a: t('pages.contact.faqG1a') },
    { category: 'general', q: t('pages.contact.faqG2q'), a: t('pages.contact.faqG2a') },
    { category: 'programs', q: t('pages.contact.faqP1q'), a: t('pages.contact.faqP1a') },
    { category: 'programs', q: t('pages.contact.faqP2q'), a: t('pages.contact.faqP2a') },
    { category: 'partnerships', q: t('pages.contact.faqPr1q'), a: t('pages.contact.faqPr1a') },
    { category: 'partnerships', q: t('pages.contact.faqPr2q'), a: t('pages.contact.faqPr2a') },
  ], [t]);

  const tabLabels = useMemo(() => ({
    general: t('pages.contact.tabGeneral'),
    programs: t('pages.contact.tabPrograms'),
    partnerships: t('pages.contact.tabPartnerships'),
  }), [t]);

  const inputStyle = {
    width: '100%', padding: '12px 14px', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10, fontSize: 14, fontFamily: 'inherit', outline: 'none',
    background: 'rgba(0,0,0,0.18)', color: 'var(--fg-text-hi)', boxSizing: 'border-box',
    boxShadow: 'var(--clay-shadow-inset-field)', transition: 'border-color .2s',
  };
  const focus = (e) => { e.target.style.borderColor = 'rgba(255,203,71,0.4)'; };
  const blur = (e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; };

  return (
    <div className="contact-page" style={{ minHeight: '100vh', fontFamily: "'DM Sans', -apple-system, sans-serif", overflowX: 'hidden', color: 'var(--fg-text-hi)' }}>
      <SEO
        titleKey="pages.seo.contact.title"
        descriptionKey="pages.seo.contact.description"
        keywordsKey="pages.seo.contact.keywords"
        path="/contact"
        breadcrumbLabel="Contact"
      />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }

        .cu-grid {
          display: grid;
          grid-template-columns: 1fr 420px;
          gap: 24px;
          align-items: start;
          padding: 32px clamp(14px,4vw,48px) 60px;
          max-width: 1200px;
          margin: 0 auto;
        }
        @media (max-width: 900px) {
          .cu-grid { grid-template-columns: 1fr; padding: 20px 14px 48px; gap: 18px; }
        }

        .cu-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 20px;
        }
        @media (max-width: 480px) {
          .cu-info-grid { grid-template-columns: 1fr; gap: 10px; }
        }

        .cu-social-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;
        }
        @media (max-width: 480px) {
          .cu-social-grid { grid-template-columns: repeat(2, 1fr); }
        }

        .cu-faq-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 14px;
          flex-wrap: wrap;
        }
        .cu-tab {
          padding: 7px 16px;
          border-radius: 20px;
          border: 1px solid rgba(255,255,255,0.16);
          background: rgba(255,255,255,0.06);
          color: var(--fg-text-mid);
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          transition: all .15s;
        }
        .cu-tab.active { background: var(--fg-gold-gradient); color: #3a2a05; border-color: transparent; }
        .cu-tab:hover:not(.active) { border-color: var(--fg-gold); color: var(--fg-gold-bright); }

        .cu-social-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
          padding: 12px 8px;
          border-radius: 12px;
          text-decoration: none;
          font-size: 12px;
          font-weight: 600;
          transition: transform .2s, box-shadow .2s;
          font-family: inherit;
        }
        .cu-social-btn:hover { transform: translateY(-3px); box-shadow: 0 6px 16px rgba(0,0,0,0.28); }

        .cu-info-card {
          display: flex;
          align-items: center;
          gap: 12px;
          background: var(--fg-glass-fill);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-radius: 12px;
          padding: 16px;
          border: var(--fg-glass-border);
          box-shadow: var(--fg-glass-shadow);
          transition: transform .2s;
        }
        .cu-info-card:hover { transform: translateY(-2px); }

        .cu-faq-item {
          background: rgba(255,255,255,0.05);
          border-radius: 10px;
          padding: 14px 16px;
          margin-bottom: 10px;
          border: 1px solid rgba(255,255,255,0.1);
          border-left: 3px solid var(--fg-gold);
          animation: fadeUp .3s ease;
        }

        .cu-submit-btn {
          width: 100%;
          padding: 14px;
          background: var(--fg-gold-gradient);
          color: #3a2a05;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: inherit;
          transition: all .2s;
          margin-top: 4px;
          box-shadow: 0 10px 28px rgba(214, 154, 23, 0.4), 0 1px 0 rgba(255, 255, 255, 0.5) inset;
        }
        .cu-submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(214, 154, 23, 0.5), 0 1px 0 rgba(255, 255, 255, 0.5) inset; }
        .cu-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
      `}</style>

      {/* ── HERO ── */}
      <div style={{ background: 'linear-gradient(135deg, #32614d 0%, #2a5240 100%)', color: 'white', padding: 'clamp(36px,6vw,60px) clamp(16px,4vw,48px) clamp(28px,5vw,48px)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Cpath d='M20 20v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E\")" }} />
        <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'inline-block', background: 'rgba(255,203,71,0.18)', color: 'var(--fg-gold-bright)', border: '1px solid rgba(255,203,71,0.35)', borderRadius: 20, padding: '4px 14px', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>
            {t('pages.contact.badge')}
          </div>
          <h1 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(24px,5vw,44px)', fontWeight: 800, margin: '0 0 12px', lineHeight: 1.15 }}>{t('pages.contact.title')}</h1>
          <p style={{ fontSize: 'clamp(13px,2vw,17px)', opacity: 0.8, maxWidth: 520, margin: 0, lineHeight: 1.65 }}>
            {t('pages.contact.subtitle')}
          </p>
        </div>
      </div>

      {/* ── MAIN GRID ── */}
      <div className="cu-grid">

        {/* ── LEFT ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* Contact info */}
          <div className="cu-info-grid">
            {contactInfo.map((info, i) => (
              <div key={i} className="cu-info-card">
                <div style={{ width: 42, height: 42, borderRadius: 10, background: info.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
                  {info.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-text-faint)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{info.title}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-text-hi)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.detail}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick AI chat — for a fast answer without waiting on the form's reply time */}
          <div style={{ background: 'var(--fg-glass-fill)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 14, padding: '18px 16px', border: 'var(--fg-glass-border)', boxShadow: 'var(--fg-glass-shadow)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-gold-bright)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 7 }}>
              <MessageCircle size={16} /> {t('pages.contact.quickAskTitle')}
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--fg-text-faint)', margin: '0 0 12px' }}>{t('pages.contact.quickAskSubtitle')}</p>
            <SupportChatPanel />
          </div>

          {/* Social */}
          <div style={{ background: 'var(--fg-glass-fill)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 14, padding: '18px 16px', border: 'var(--fg-glass-border)', boxShadow: 'var(--fg-glass-shadow)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-gold-bright)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 7 }}>
              <MessageCircle size={16} /> {t('pages.contact.followJourney')}
            </div>
            <div className="cu-social-grid">
              {socialLinks.map((s, i) => (
                <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" className="cu-social-btn" style={{ background: s.bg, color: s.color }}>
                  {s.icon}
                  <span>{s.label}</span>
                </a>
              ))}
            </div>
          </div>

          {/* FAQs */}
          <div style={{ background: 'var(--fg-glass-fill)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 14, padding: '18px 16px', border: 'var(--fg-glass-border)', boxShadow: 'var(--fg-glass-shadow)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-gold-bright)', marginBottom: 14 }}>{t('pages.contact.faqTitle')}</div>
            <div className="cu-faq-tabs">
              {['general', 'programs', 'partnerships'].map(tab => (
                <button key={tab} className={`cu-tab${activeTab === tab ? ' active' : ''}`} onClick={() => setActiveTab(tab)}>
                  {tabLabels[tab]}
                </button>
              ))}
            </div>
            <div>
              {faqs.filter(f => f.category === activeTab).map((faq, i) => (
                <div key={i} className="cu-faq-item">
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-text-hi)', marginBottom: 5 }}>{faq.q}</div>
                  <p style={{ fontSize: 13, color: 'var(--fg-text-mid)', lineHeight: 1.6, margin: 0 }}>{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── RIGHT — form ── */}
        <div style={{ background: 'var(--fg-glass-fill)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 16, padding: 'clamp(18px,4vw,28px)', border: 'var(--fg-glass-border)', boxShadow: 'var(--fg-glass-shadow)' }}>
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 'clamp(17px,3vw,22px)', fontWeight: 800, color: 'var(--fg-text-hi)', margin: '0 0 6px' }}>{t('pages.contact.sendMessage')}</h2>
            <p style={{ fontSize: 13, color: 'var(--fg-text-faint)', margin: 0 }}>{t('pages.contact.replyWithin')}</p>
          </div>

          {isSubmitted ? (
            <div style={{ textAlign: 'center', padding: '36px 16px', animation: 'fadeUp .4s ease' }}>
              <div style={{ width: 64, height: 64, background: 'var(--fg-gold-gradient)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <CheckCircle size={32} color="#3a2a05" />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--fg-gold-bright)', marginBottom: 8 }}>{t('pages.contact.sentTitle')}</h3>
              <p style={{ fontSize: 14, color: 'var(--fg-text-mid)', lineHeight: 1.6, margin: 0 }}>{t('pages.contact.sentBody')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                placeholder={t('pages.contact.yourName')} required style={inputStyle} onFocus={focus} onBlur={blur} />
              <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                placeholder={t('pages.contact.emailPlaceholder')} required style={inputStyle} onFocus={focus} onBlur={blur} />
              <textarea name="message" value={formData.message} onChange={handleInputChange}
                placeholder={t('pages.contact.messagePlaceholder')} rows="5" required
                style={{ ...inputStyle, resize: 'vertical', minHeight: 110 }} onFocus={focus} onBlur={blur} />
              {submitError && (
                <div style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 12px', fontSize: 13, fontWeight: 600 }}>
                  {submitError}
                </div>
              )}
              <button type="submit" className="cu-submit-btn" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><div style={{ width: 16, height: 16, border: '2px solid rgba(58,42,5,0.35)', borderTopColor: '#3a2a05', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /> {t('pages.contact.sending')}</>
                ) : (
                  <><Send size={16} /> {t('pages.contact.sendBtn')}</>
                )}
              </button>
            </form>
          )}

          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12, color: 'var(--fg-text-faint)' }}>
            <Clock size={13} /> {t('pages.contact.responseTime')}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div style={{ background: 'linear-gradient(135deg, #32614d, #2a5240)', padding: 'clamp(36px,6vw,56px) clamp(16px,4vw,48px)', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(20px,4vw,32px)', fontWeight: 800, color: 'white', marginBottom: 10, lineHeight: 1.2 }}>{t('pages.contact.ctaTitle')}</h2>
        <p style={{ fontSize: 'clamp(13px,2vw,16px)', color: 'rgba(255,255,255,0.75)', marginBottom: 24, maxWidth: 480, margin: '0 auto 24px', lineHeight: 1.6 }}>
          {t('pages.contact.ctaSub')}
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/programs')}
            style={{ padding: '11px 24px', background: 'var(--fg-gold-gradient)', color: '#3a2a05', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .2s', boxShadow: '0 10px 28px rgba(214, 154, 23, 0.4)' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
            {t('pages.contact.browseOpps')}
          </button>
          <button onClick={() => navigate('/whoweare')}
            style={{ padding: '11px 24px', background: 'rgba(255,255,255,0.12)', color: 'white', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .2s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}>
            {t('pages.contact.learnMore')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;